import { Account } from '@aidkitorg/aidkit/lib/admin_panel/accounts';
import { BreadcrumbProps, Button, Card, CardBody, CardHeader, DateDisplay, Icon, NumberDisplay, PieChart, Tooltip } from '@aidkitorg/component-library';
import { useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useAccounts } from '../../Contexts/AccountsContext';
import { useAsyncEffect } from '../../Hooks/AsyncEffect';
import { useVisibilityPolling } from '../../Hooks/VisibilityPolling';
import { useLocalizedStrings } from '../../Localization';
import { AdminPanelPage } from '../AdminPanelPage';
import { AccountDetailsCard } from './components/AccountDetailsCard';
import { ManageBalanceRemindersModal } from './components/ManageBalanceRemindersModal';

export const accountKindToName = (kind: Account['kind']): string => {
  switch(kind) {
    case 'usio':
      return 'USIO (Debit)'
    case 'dwolla':
      return 'Dwolla (ACH)'
    case 'mercury':
      return 'Mercury Account'
    case 'usbank':
      return 'US Bank (Debit)'
    case 'givecard':
      return 'Givecard'
    case 'givecard_rtp':
      return 'Givecard (ACH)'
    case 'givecard_debit':
      return "Givecard (Debit)"
    case 'unknown':
      return "Unknown"
  }
}

export const AccountsPage = () => {
  const L = useLocalizedStrings()
  
  const [processedAccounts, setProcessedAccounts] = useState<Array<Account>>([])
  const [manageBalanceRemindersModalOpen, setManageBalanceRemindersModalOpen] = useState<boolean>(false)

  const location = useLocation()
  const history = useHistory()
  const isOnAccountsPage = location.pathname === '/admin-panel/accounts'
  
  // Check for URL parameters to auto-open modal
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    if (searchParams.get('action') === 'manage-reminders') {
      setManageBalanceRemindersModalOpen(true)
      
      // Clean up the URL parameter after opening the modal
      searchParams.delete('action')
      const newSearch = searchParams.toString()
      const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname
      history.replace(newUrl)
    }
  }, [location.search, location.pathname, history])
    
  const { 
    totalBalance, 
    accounts, 
    accountsDataTimestamp,
    isAccountsLoading, 
    fetchAccountsOverview 
  } = useAccounts()
  
  
  useAsyncEffect(async () => {
    await fetchAccountsOverview()
  }, [])

  useVisibilityPolling({
    onImmediate: () => fetchAccountsOverview(),
    onInterval: () => fetchAccountsOverview(),
    isActive: isOnAccountsPage,
    intervalMs: 2 * 60 * 1000
  })

  useEffect(() => {
    // Rename accounts based on the account kind
    const processedAccounts = accounts.map((account) => (
      { ...account, name: accountKindToName(account.kind)}
    ))

    setProcessedAccounts(processedAccounts)
  }, [accounts])

  const totalBalanceCard = <NumberDisplay 
    title={L.admin_panel.accounts.total_available_balance}
    isCurrency={true}
    locale="en-US"
    currency="USD"
    iconColor='blue'
    iconName='WalletIcon'
    className="w-full h-full"
    value={totalBalance ? 0.01 * totalBalance : undefined } 
    loading={isAccountsLoading}
  />

  const accountsPieChartCard = <Card>
    <CardHeader title={<div className="font-medium">{L.admin_panel.accounts.payment_provider_share}</div>}/>
    <CardBody>
      <PieChart 
        data={processedAccounts} 
        nameKey='name' 
        dataKey='totalBalance' 
        width="50%" 
        height={isAccountsLoading ? 300 : 350}
        showLegend
        showLabels={false}
      />
    </CardBody>
  </Card>

  const accountDetailsCards = processedAccounts
    .sort((a, b) => b.totalBalance - a.totalBalance)
    .map((account, idx) => {
      return (
        <div key={idx}>
          <AccountDetailsCard index={idx} account={account} className="h-full"/>
        </div>
      )
    })

  const breadcrumbs: BreadcrumbProps['items'] = [
    {
      label: L.admin_panel.nav.accounting,
    }
  ];

  return (
    <AdminPanelPage
      title={L.admin_panel.accounts.accounts}
      breadcrumbs={breadcrumbs}
      pageActionButtons={[
        <Tooltip text={L.admin_panel.payments.refresh_data} asChild>
          <Button
            icon={<Icon role="decorative" type="solid" name="ArrowPathIcon" />}
            variant="secondary"
            aria-label={L.admin_panel.payments.refresh_data}
            onClick={async () => { await fetchAccountsOverview(true) }}
          />
        </Tooltip>,
        <Button
          variant="primary"
          icon={<Icon name="CalendarIcon" type="solid" role="decorative" />}
          onClick={() => setManageBalanceRemindersModalOpen(true)}
          aria-label={L.admin_panel.accounts.manage_reminders}
        >
          {L.admin_panel.accounts.manage_reminders}
        </Button>
      ]}
    >
      <div className="flex flex-col gap-2 w-full h-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <div className="lg:col-span-1">
            {totalBalanceCard}
          </div>
          <div className="lg:col-span-3">
            {accountsPieChartCard}
          </div>
        </div>
        <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 md:grid-cols-4 items-stretch">
          {accountDetailsCards}
        </div>

        {accountsDataTimestamp && (
          <div className="w-full flex flex-row">
            <div className="text-xs font-medium text-gray-500">
              <span>{L.admin_panel.payments.last_updated}:&nbsp;</span><DateDisplay className="text-gray-500 text-xs font-medium" utcValue={accountsDataTimestamp} showRelativeTime />
            </div>
          </div>
        )}
      </div>

      <ManageBalanceRemindersModal
        remindersModalOpen={manageBalanceRemindersModalOpen}
        setRemindersModalOpen={setManageBalanceRemindersModalOpen}
      />
    </AdminPanelPage>
  )
}